(function () {
  const instances = new WeakMap();

  class DSHeroSlideshow {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.ds-hero-slideshow__viewport');
      this.slides = Array.from(root.querySelectorAll('[data-ds-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-ds-dot]'));
      this.prevButton = root.querySelector('[data-ds-prev]');
      this.nextButton = root.querySelector('[data-ds-next]');

      this.currentIndex = 0;
      this.timer = null;

      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.autoplayMs = Number(root.dataset.autoplayMs || 6000);
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.isDragging = false;
      this.isSwiping = false;
      this.didSwipe = false;
      this.startX = 0;
      this.startY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.swipeThreshold = 45;
      this.pointerId = null;

      this.onPrevClick = this.previousFromClick.bind(this);
      this.onNextClick = this.nextFromClick.bind(this);

      this.onMouseEnter = this.stop.bind(this);
      this.onMouseLeave = this.start.bind(this);
      this.onFocusIn = this.stop.bind(this);
      this.onFocusOut = this.start.bind(this);

      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onPointerCancel = this.handlePointerCancel.bind(this);

      this.onMouseDown = this.handleMouseDown.bind(this);
      this.onMouseMove = this.handleMouseMove.bind(this);
      this.onMouseUp = this.handleMouseUp.bind(this);

      this.onTouchStart = this.handleTouchStart.bind(this);
      this.onTouchMove = this.handleTouchMove.bind(this);
      this.onTouchEnd = this.handleTouchEnd.bind(this);

      this.onClickCapture = this.handleClickCapture.bind(this);
      this.onResize = this.syncVideos.bind(this);

      this.init();
    }

    init() {
      if (!this.root || this.slides.length === 0) return;

      this.goTo(0, false);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.onNextClick);
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const index = Number(dot.dataset.dsDot);
          this.goTo(index);
          this.restart();
        });
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.onMouseEnter);
        this.root.addEventListener('mouseleave', this.onMouseLeave);
        this.root.addEventListener('focusin', this.onFocusIn);
        this.root.addEventListener('focusout', this.onFocusOut);
      }

      window.addEventListener('resize', this.onResize);

      this.bindSwipeEvents();
      this.syncVideos();
      this.start();
    }

    previousFromClick(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.previous();
    }

    nextFromClick(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.next();
    }

    bindSwipeEvents() {
      if (!this.viewport || this.slides.length < 2) return;

      this.root.addEventListener('click', this.onClickCapture, true);

      if (window.PointerEvent) {
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerCancel);
      } else {
        this.viewport.addEventListener('mousedown', this.onMouseDown);
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);

        this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: true });
        this.viewport.addEventListener('touchend', this.onTouchEnd);
      }
    }

    shouldIgnoreDragStart(eventTarget) {
      if (!eventTarget) return false;

      return Boolean(
        eventTarget.closest(
          '.ds-hero-slideshow__arrow, .ds-hero-slideshow__dot, .ds-hero-slideshow__button'
        )
      );
    }

    beginDrag(x, y) {
      this.isDragging = true;
      this.isSwiping = false;
      this.didSwipe = false;
      this.startX = x;
      this.startY = y;
      this.currentX = x;
      this.currentY = y;

      this.stop();
    }

    updateDrag(x, y) {
      if (!this.isDragging) return;

      this.currentX = x;
      this.currentY = y;

      const deltaX = this.currentX - this.startX;
      const deltaY = this.currentY - this.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX > 10 && absX > absY) {
        this.isSwiping = true;
        this.root.classList.add('is-swiping');
      }
    }

    endDrag() {
      if (!this.isDragging) return;

      const deltaX = this.currentX - this.startX;
      const deltaY = this.currentY - this.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (this.isSwiping && absX > this.swipeThreshold && absX > absY) {
        this.didSwipe = true;

        if (deltaX < 0) {
          this.next();
        } else {
          this.previous();
        }
      }

      this.resetDragState();
      this.restart();
    }

    resetDragState() {
      this.isDragging = false;
      this.isSwiping = false;
      this.startX = 0;
      this.startY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.pointerId = null;

      this.root.classList.remove('is-swiping');
    }

    handlePointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (event.isPrimary === false) return;
      if (this.shouldIgnoreDragStart(event.target)) return;

      this.pointerId = event.pointerId;
      this.beginDrag(event.clientX, event.clientY);

      if (this.viewport && this.viewport.setPointerCapture) {
        try {
          this.viewport.setPointerCapture(this.pointerId);
        } catch (error) {}
      }
    }

    handlePointerMove(event) {
      if (!this.isDragging) return;
      if (this.pointerId !== null && event.pointerId !== this.pointerId) return;

      this.updateDrag(event.clientX, event.clientY);
    }

    handlePointerUp(event) {
      if (!this.isDragging) return;
      if (this.pointerId !== null && event.pointerId !== this.pointerId) return;

      if (this.viewport && this.viewport.releasePointerCapture) {
        try {
          this.viewport.releasePointerCapture(this.pointerId);
        } catch (error) {}
      }

      this.endDrag();
    }

    handlePointerCancel() {
      this.resetDragState();
      this.restart();
    }

    handleMouseDown(event) {
      if (event.button !== 0) return;
      if (this.shouldIgnoreDragStart(event.target)) return;

      this.beginDrag(event.clientX, event.clientY);
    }

    handleMouseMove(event) {
      this.updateDrag(event.clientX, event.clientY);
    }

    handleMouseUp() {
      this.endDrag();
    }

    handleTouchStart(event) {
      if (!event.touches || !event.touches.length) return;
      if (this.shouldIgnoreDragStart(event.target)) return;

      const touch = event.touches[0];
      this.beginDrag(touch.clientX, touch.clientY);
    }

    handleTouchMove(event) {
      if (!event.touches || !event.touches.length) return;

      const touch = event.touches[0];
      this.updateDrag(touch.clientX, touch.clientY);
    }

    handleTouchEnd() {
      this.endDrag();
    }

    handleClickCapture(event) {
      if (!this.didSwipe) return;

      event.preventDefault();
      event.stopPropagation();

      this.didSwipe = false;
    }

    isVideoVisible(video) {
      if (!video) return false;

      const styles = window.getComputedStyle(video);

      return styles.display !== 'none' && styles.visibility !== 'hidden' && styles.opacity !== '0';
    }

    syncVideos() {
      this.slides.forEach((slide) => {
        const isActive = slide.classList.contains('is-active');
        const videos = Array.from(slide.querySelectorAll('video'));

        videos.forEach((video) => {
          video.muted = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');

          const shouldPlay = isActive && this.isVideoVisible(video);

          if (shouldPlay) {
            const playPromise = video.play();

            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();

            try {
              video.currentTime = 0;
            } catch (error) {}
          }
        });
      });
    }

    goTo(index, shouldRestart = true) {
      if (!this.slides.length) return;

      const nextIndex = (index + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;

        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === nextIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.currentIndex = nextIndex;
      this.syncVideos();

      if (shouldRestart) {
        this.restart();
      }
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    previous() {
      this.goTo(this.currentIndex - 1);
    }

    start() {
      if (!this.autoplayEnabled) return;
      if (this.prefersReducedMotion) return;
      if (this.slides.length < 2) return;

      this.stop();

      this.timer = window.setInterval(() => {
        this.next();
      }, this.autoplayMs);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      this.stop();
      this.start();
    }

    destroy() {
      this.stop();

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.onNextClick);
      }

      if (this.pauseOnHover) {
        this.root.removeEventListener('mouseenter', this.onMouseEnter);
        this.root.removeEventListener('mouseleave', this.onMouseLeave);
        this.root.removeEventListener('focusin', this.onFocusIn);
        this.root.removeEventListener('focusout', this.onFocusOut);
      }

      window.removeEventListener('resize', this.onResize);

      if (this.viewport) {
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        this.viewport.removeEventListener('pointerup', this.onPointerUp);
        this.viewport.removeEventListener('pointercancel', this.onPointerCancel);

        this.viewport.removeEventListener('mousedown', this.onMouseDown);
        this.viewport.removeEventListener('touchstart', this.onTouchStart);
        this.viewport.removeEventListener('touchmove', this.onTouchMove);
        this.viewport.removeEventListener('touchend', this.onTouchEnd);
      }

      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);

      this.root.removeEventListener('click', this.onClickCapture, true);
    }
  }

  function initHeroSlideshows(scope) {
    const root = scope || document;

    root.querySelectorAll('[data-ds-hero-slideshow]').forEach((hero) => {
      if (!instances.has(hero)) {
        instances.set(hero, new DSHeroSlideshow(hero));
      }
    });
  }

  function destroyHeroSlideshows(scope) {
    const root = scope || document;

    root.querySelectorAll('[data-ds-hero-slideshow]').forEach((hero) => {
      const instance = instances.get(hero);

      if (instance) {
        instance.destroy();
        instances.delete(hero);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initHeroSlideshows(document);
    });
  } else {
    initHeroSlideshows(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initHeroSlideshows(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyHeroSlideshows(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-ds-slide]');
    const hero = event.target.closest('[data-ds-hero-slideshow]');

    if (!slide || !hero) return;

    const instance = instances.get(hero);

    if (!instance) return;

    const index = Number(slide.dataset.dsSlideIndex || 0);

    instance.goTo(index, false);
    instance.stop();
    instance.syncVideos();
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const hero = event.target.closest('[data-ds-hero-slideshow]');

    if (!hero) return;

    const instance = instances.get(hero);

    if (instance) {
      instance.syncVideos();
      instance.start();
    }
  });
})();